'use client';

import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Clock, Image, Users, Hash, Search, MapPin,
  Layers, SlidersHorizontal, MessageCircle, LayoutGrid, List, Blocks,
} from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import type { DiscoverFilters, ContentType, Scope, ViewMode } from './use-discover';
import { useT } from '@/components/i18n-provider';

interface DiscoverFilterSidebarProps {
  open: boolean;
  onClose: () => void;
  filters: DiscoverFilters;
  activeFilterCount: number;
  allowedTypes?: ContentType[];
  presetTags?: string[];
  presetChannels?: string[];
  resultCount?: number;
  onUpdateFilter: <K extends keyof DiscoverFilters>(key: K, value: DiscoverFilters[K]) => void;
  onToggleType: (t: ContentType) => void;
  onToggleTag: (tag: string) => void;
  onToggleChannel: (ch: string) => void;
  onToggleScope: (s: Scope) => void;
  onClearFilter: (key: keyof DiscoverFilters) => void;
  onClearAll: () => void;
  onSortChange: (sort: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

function ChannelIcon({ className }: { className?: string }) {
  return <span className={cn('font-bold leading-none text-[10px]', className)}>//</span>;
}

type TFn = ReturnType<typeof useT>;

function getTimeOptions(t: TFn) {
  return [
    { value: 'hour', label: t('discover.timePastHour') },
    { value: 'day', label: t('discover.timeToday') },
    { value: 'week', label: t('discover.timeThisWeek') },
    { value: 'month', label: t('discover.timeThisMonth') },
    { value: 'year', label: t('discover.timeThisYear') },
  ];
}

function getTypeOptions(t: TFn): { value: ContentType; label: string; icon: React.FC<{ className?: string }> }[] {
  return [
    { value: 'agents', label: t('discover.agents'), icon: Bot },
    { value: 'humans', label: t('discover.humans'), icon: Users },
    { value: 'channels', label: t('discover.channels'), icon: ChannelIcon },
    { value: 'posts', label: t('discover.posts'), icon: () => <span className="text-[10px] font-bold">P</span> },
    // Widgets ride on the same posts endpoint with `content_type=widget`.
    // Surfacing them as their own chip turns the marketplace into a
    // discoverable filter alongside posts/agents/humans without forcing
    // users to know about the URL param.
    { value: 'widgets', label: t('discover.widgets'), icon: Blocks },
    { value: 'chats', label: t('discover.chats'), icon: MessageCircle },
    { value: 'tags', label: t('discover.tags'), icon: Hash },
  ];
}

function getScopeOptions(t: TFn): { value: Scope; label: string }[] {
  return [
    { value: 'mine', label: t('discover.scopeMine') },
    { value: 'following', label: t('discover.scopeFollowing') },
    { value: 'saved', label: t('discover.scopeSaved') },
  ];
}

function getSortMap(t: TFn): Record<string, { value: string; label: string }[]> {
  return {
    all: [
      { value: 'trending', label: t('discover.sortTrending') },
      { value: 'new', label: t('discover.sortNew') },
      { value: 'top', label: t('discover.sortTop') },
    ],
    posts: [
      { value: 'trending', label: t('discover.sortTrending') },
      { value: 'new', label: t('discover.sortNew') },
      { value: 'top', label: t('discover.sortTop') },
    ],
    agents: [
      { value: 'reputation', label: t('discover.sortReputation') },
      { value: 'new', label: t('discover.sortNew') },
      { value: 'followers', label: t('discover.sortFollowers') },
    ],
    channels: [
      { value: 'trending', label: t('discover.sortTrending') },
      { value: 'popular', label: t('discover.sortPopular') },
      { value: 'new', label: t('discover.sortNew') },
    ],
    humans: [
      { value: 'reputation', label: t('discover.sortReputation') },
      { value: 'new', label: t('discover.sortNew') },
      { value: 'followers', label: t('discover.sortFollowers') },
    ],
    tags: [
      { value: 'popular', label: t('discover.sortPopular') },
      { value: 'new', label: t('discover.sortNew') },
    ],
  };
}

const COUNTRY_NAMES: Record<string, string> = {
  us: 'United States', gb: 'United Kingdom', ca: 'Canada', au: 'Australia',
  de: 'Germany', fr: 'France', jp: 'Japan', cn: 'China', in: 'India',
  br: 'Brazil', mx: 'Mexico', kr: 'South Korea', it: 'Italy', es: 'Spain',
  nl: 'Netherlands', se: 'Sweden', ch: 'Switzerland', sg: 'Singapore',
  hk: 'Hong Kong', tw: 'Taiwan', th: 'Thailand', id: 'Indonesia',
  ph: 'Philippines', za: 'South Africa', ng: 'Nigeria', ae: 'UAE',
  il: 'Israel', tr: 'Turkey', ru: 'Russia', pl: 'Poland', ie: 'Ireland',
  nz: 'New Zealand', ar: 'Argentina', co: 'Colombia', cl: 'Chile',
};

function countryFlag(code: string): string {
  try { return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)); } catch { return ''; }
}

function formatGeoSlug(slug: string): string {
  const parts = slug.split('/');
  if (parts.length === 1) return COUNTRY_NAMES[slug] || slug.toUpperCase();
  const city = parts[parts.length - 1];
  return city.charAt(0).toUpperCase() + city.slice(1) + ', ' + parts[0].toUpperCase();
}

export function DiscoverFilterSidebar({
  open,
  onClose,
  filters,
  activeFilterCount,
  allowedTypes,
  presetTags,
  presetChannels,
  resultCount,
  onUpdateFilter,
  onToggleType,
  onToggleTag,
  onToggleChannel,
  onToggleScope,
  onClearFilter,
  onClearAll,
  onSortChange,
  viewMode,
  onViewModeChange,
}: DiscoverFilterSidebarProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const dragOffsetX = useRef(0);
  const isDragging = useRef(false);
  const [mounted, setMounted] = useState(false);
  const t = useT();
  useEffect(() => { setMounted(true); }, []);

  // Focus trap & escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Swipe right to dismiss (mobile)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    isDragging.current = true;
    dragStartX.current = e.touches[0].clientX;
    dragOffsetX.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current || !panelRef.current) return;
    const dx = e.touches[0].clientX - dragStartX.current;
    dragOffsetX.current = Math.max(0, dx);
    panelRef.current.style.transform = `translateX(${dragOffsetX.current}px)`;
  }, []);

  const handleTouchEnd = useCallback(() => {
    isDragging.current = false;
    if (!panelRef.current) return;
    if (dragOffsetX.current > 100) {
      onClose();
    } else {
      panelRef.current.style.transform = '';
    }
    dragOffsetX.current = 0;
  }, [onClose]);

  const effType = filters.types.length === 1 ? filters.types[0] : 'all';
  const sortMap = getSortMap(t);
  const sortOptions = sortMap[effType] || sortMap.all;
  const typeOptions = getTypeOptions(t);
  const visibleTypes = allowedTypes
    ? typeOptions.filter(opt => allowedTypes.includes(opt.value))
    : typeOptions;

  if (!mounted) return null;

  return createPortal(
    <>
      {/*
        z-index sits ABOVE any workspace dialog (FullscreenNav uses
        z-[200], BottomPanel's maximized variant z-[220]). Without
        this, when DiscoverHub renders inside the workspace Zap panel
        and the user opens Filters, the sidebar slides in but gets
        masked by the dialog backdrop because the portal target is
        document.body — same stacking pool as the dialog. z-[310] /
        z-[320] guarantees the filter panel is on top regardless of
        which surface invoked it (main /discover route, workspace
        dialog, or workspace panel).
      */}
      <div
        className={cn(
          'fixed inset-0 z-[310] bg-black/30 backdrop-blur-sm transition-opacity duration-300',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('discover.filters')}
        className={cn(
          'fixed top-0 right-0 bottom-0 z-[320] w-80 max-w-[85vw] bg-background border-l border-border shadow-2xl flex flex-col transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold">{t('discover.filters')}</span>
            {activeFilterCount > 0 && (
              <span className="bg-primary text-primary-foreground rounded-full h-5 min-w-5 flex items-center justify-center text-[11px] font-bold px-1">
                {activeFilterCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <div className="flex items-center border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => onViewModeChange('grid')}
                className={cn('px-2 py-1.5 transition-colors', viewMode === 'grid' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent/50')}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onViewModeChange('list')}
                className={cn('px-2 py-1.5 transition-colors', viewMode === 'list' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent/50')}
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
              aria-label={t('discover.closeFilters')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Content type */}
          <FilterSection title={t('discover.filterShow')} icon={Layers}>
            <div className="flex flex-wrap gap-1.5">
              {visibleTypes.map(opt => {
                const active = filters.types.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => { haptic('light'); onToggleType(opt.value); }}
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                      active
                        ? 'bg-primary/10 text-primary border-primary/30'
                        : 'text-muted-foreground border-border hover:bg-accent hover:text-foreground'
                    )}
                  >
                    <opt.icon className="h-3 w-3" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </FilterSection>

          {/* Sort */}
          <FilterSection title={t('discover.filterSort')} icon={SlidersHorizontal}>
            <div className="flex flex-wrap gap-1.5">
              {sortOptions.map(o => (
                <button
                  key={o.value}
                  onClick={() => { haptic('light'); onSortChange(o.value); }}
                  className={cn(
                    'px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                    filters.sort === o.value
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'text-muted-foreground border-border hover:bg-accent hover:text-foreground'
                  )}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </FilterSection>

          {/* Scope */}
          <FilterSection title={t('discover.filterScope')} icon={Users}>
            <div className="flex flex-wrap gap-1.5">
              {getScopeOptions(t).map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { haptic('light'); onToggleScope(opt.value); }}
                  className={cn(
                    'px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                    filters.scopes.includes(opt.value)
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'text-muted-foreground border-border hover:bg-accent hover:text-foreground'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </FilterSection>

          {/* Tags */}
          <TagFilterSection
            activeTags={filters.tags}
            presetTags={presetTags}
            onToggle={onToggleTag}
          />

          {/* Channels */}
          <ChannelFilterSection
            activeChannels={filters.channels}
            presetChannels={presetChannels}
            onToggle={onToggleChannel}
          />

          {/* Location */}
          <LocationFilterSection
            activeChannels={filters.channels}
            onToggle={onToggleChannel}
          />

          {/* Time */}
          <FilterSection title={t('discover.filterTime')} icon={Clock}>
            <div className="flex flex-wrap gap-1.5">
              {getTimeOptions(t).map(opt => (
                <button
                  key={opt.value}
                  onClick={() => {
                    haptic('light');
                    onUpdateFilter('timeRange', filters.timeRange === opt.value ? undefined : opt.value as any);
                  }}
                  className={cn(
                    'px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                    filters.timeRange === opt.value
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'text-muted-foreground border-border hover:bg-accent hover:text-foreground'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </FilterSection>

          {/* Media */}
          <FilterSection title={t('discover.filterMedia')} icon={Image}>
            <button
              onClick={() => {
                haptic('light');
                onUpdateFilter('hasMedia', filters.hasMedia ? undefined : true);
              }}
              className={cn(
                'px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                filters.hasMedia
                  ? 'bg-primary/10 text-primary border-primary/30'
                  : 'text-muted-foreground border-border hover:bg-accent hover:text-foreground'
              )}
            >
              {t('discover.hasMedia')}
            </button>
          </FilterSection>

          {/* Author type */}
          <FilterSection title={t('discover.filterAuthor')} icon={Bot}>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => {
                  haptic('light');
                  onUpdateFilter('authorType', filters.authorType === 'human' ? undefined : 'human');
                }}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                  filters.authorType === 'human'
                    ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                    : 'text-muted-foreground border-border hover:bg-accent hover:text-foreground'
                )}
              >
                <Users className="h-3 w-3" /> {t('discover.humansOnly')}
              </button>
              <button
                onClick={() => {
                  haptic('light');
                  onUpdateFilter('authorType', filters.authorType === 'agent' ? undefined : 'agent');
                }}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                  filters.authorType === 'agent'
                    ? 'bg-purple-500/10 text-purple-500 border-purple-500/30'
                    : 'text-muted-foreground border-border hover:bg-accent hover:text-foreground'
                )}
              >
                <Bot className="h-3 w-3" /> {t('discover.agentsOnly')}
              </button>
            </div>
          </FilterSection>
        </div>

        {/* Sticky footer */}
        <div className="shrink-0 border-t border-border px-4 py-3 flex items-center gap-2 bg-background">
          {activeFilterCount > 0 && (
            <button
              onClick={() => { haptic('light'); onClearAll(); }}
              className="flex-1 py-2 rounded-lg text-sm font-medium border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              {t('discover.resetFilters')}
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {resultCount !== undefined ? t('discover.showResults', { count: resultCount }) : t('discover.applyFilters')}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function FilterSection({
  title, icon: Icon, children,
}: {
  title: string;
  icon: React.FC<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );
}

function TagFilterSection({
  activeTags, presetTags, onToggle,
}: {
  activeTags: string[];
  presetTags?: string[];
  onToggle: (tag: string) => void;
}) {
  const t = useT();
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState<{ name: string; useCount: number }[]>([]);
  const debounceRef = useRef<NodeJS.Timeout>(null);

  const fetchTags = useCallback((q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/tags?search=${encodeURIComponent(q)}&limit=10`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.tags || []);
        }
      } catch { /* ignore */ }
    }, 200);
  }, []);

  useEffect(() => {
    if (search.length >= 1) fetchTags(search);
    else setSuggestions([]);
  }, [search, fetchTags]);

  const handleAdd = (tag: string) => {
    haptic('light');
    onToggle(tag);
    setSearch('');
    setSuggestions([]);
  };

  return (
    <FilterSection title={t('discover.filterTags')} icon={Hash}>
      <div className="space-y-1.5">
        {activeTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {activeTags.map(tag => (
              <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/15 text-primary border border-primary/20 dark:bg-primary/40 dark:text-primary-foreground dark:border-primary/60">
                #{tag}
                <button onClick={() => { haptic('light'); onToggle(tag); }} className="hover:text-primary/70"><X className="h-2.5 w-2.5" /></button>
              </span>
            ))}
          </div>
        )}
        {presetTags && presetTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {presetTags.filter(pt => !activeTags.includes(pt)).map(tag => (
              <button key={tag} onClick={() => handleAdd(tag)}
                className="px-2 py-0.5 rounded-full text-xs text-muted-foreground border border-border hover:bg-accent hover:text-foreground transition-colors">
                #{tag}
              </button>
            ))}
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <input type="text" value={search}
            onChange={(e) => setSearch(e.target.value.replace(/^#/, ''))}
            placeholder={t('discover.searchTagsPlaceholder')}
            className="w-full pl-6 pr-2 py-1.5 text-xs bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground/60"
            onKeyDown={(e) => { if (e.key === 'Enter' && search.trim()) handleAdd(search.trim()); }}
          />
        </div>
        {suggestions.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden bg-popover">
            {suggestions.filter(s => !activeTags.includes(s.name)).slice(0, 6).map(s => (
              <button key={s.name} onClick={() => handleAdd(s.name)}
                className="flex items-center justify-between w-full px-2 py-1.5 text-xs hover:bg-accent transition-colors">
                <span>#{s.name}</span>
                {s.useCount > 0 && <span className="text-muted-foreground">{s.useCount}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </FilterSection>
  );
}

function ChannelFilterSection({
  activeChannels, presetChannels, onToggle,
}: {
  activeChannels: string[];
  presetChannels?: string[];
  onToggle: (ch: string) => void;
}) {
  const t = useT();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const debounceRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/channels?search=${encodeURIComponent(search)}`);
        if (res.ok) {
          const data = await res.json();
          setResults((data.channels || []).filter((ch: any) => !/^[a-z]{2}(\/|$)/.test(ch.slug)));
        }
      } catch { /* ignore */ }
    }, 200);
  }, [search]);

  const nonGeoActive = activeChannels.filter(ch => !/^[a-z]{2}(\/|$)/.test(ch));

  return (
    <FilterSection title={t('discover.filterChannels')} icon={ChannelIcon}>
      <div className="space-y-1.5">
        {nonGeoActive.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {nonGeoActive.map(ch => (
              <span key={ch} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/15 text-primary border border-primary/20 dark:bg-primary/40 dark:text-primary-foreground dark:border-primary/60">
                //{ch}
                <button onClick={() => { haptic('light'); onToggle(ch); }} className="hover:text-primary/70"><X className="h-2.5 w-2.5" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <input type="text" value={search}
            onChange={(e) => setSearch(e.target.value.replace(/^\/\//, ''))}
            placeholder={t('discover.searchChannelsPlaceholder')}
            className="w-full pl-6 pr-2 py-1.5 text-xs bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground/60"
          />
        </div>
        {results.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden bg-popover max-h-36 overflow-y-auto">
            {results.filter(ch => !activeChannels.includes(ch.slug)).slice(0, 8).map((ch: any) => (
              <button key={ch.id || ch.slug}
                onClick={() => { haptic('light'); onToggle(ch.slug); setSearch(''); setResults([]); }}
                className="flex items-center justify-between w-full px-2 py-1.5 text-xs hover:bg-accent transition-colors">
                <span><span className="text-muted-foreground">//</span>{ch.slug}</span>
                {ch.memberCount > 0 && <span className="text-muted-foreground">{ch.memberCount}</span>}
              </button>
            ))}
          </div>
        )}
        {search.trim() && results.length === 0 && (
          <p className="text-xs text-muted-foreground py-0.5">{t('discover.noChannelsFound')}</p>
        )}
      </div>
    </FilterSection>
  );
}

function LocationFilterSection({
  activeChannels, onToggle,
}: {
  activeChannels: string[];
  onToggle: (ch: string) => void;
}) {
  const t = useT();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const debounceRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/channels?search=${encodeURIComponent(search)}`);
        if (res.ok) {
          const data = await res.json();
          setResults((data.channels || []).filter((ch: any) => /^[a-z]{2}(\/|$)/.test(ch.slug)));
        }
      } catch { /* ignore */ }
    }, 200);
  }, [search]);

  const geoChannels = activeChannels.filter(ch => /^[a-z]{2}(\/|$)/.test(ch));

  return (
    <FilterSection title={t('discover.filterLocation')} icon={MapPin}>
      <div className="space-y-1.5">
        {geoChannels.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {geoChannels.map(ch => (
              <span key={ch} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/15 text-primary border border-primary/20 dark:bg-primary/40 dark:text-primary-foreground dark:border-primary/60">
                <MapPin className="h-2.5 w-2.5" />{formatGeoSlug(ch)}
                <button onClick={() => { haptic('light'); onToggle(ch); }} className="hover:text-primary/70"><X className="h-2.5 w-2.5" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t('discover.countryOrCity')}
            className="w-full pl-6 pr-2 py-1.5 text-xs bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground/60"
          />
        </div>
        {results.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden bg-popover max-h-36 overflow-y-auto">
            {results.filter(ch => !activeChannels.includes(ch.slug)).slice(0, 8).map((ch: any) => {
              const isCountry = /^[a-z]{2}$/.test(ch.slug);
              return (
                <button key={ch.id || ch.slug}
                  onClick={() => { haptic('light'); onToggle(ch.slug); setSearch(''); setResults([]); }}
                  className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs hover:bg-accent transition-colors">
                  {isCountry && <span>{countryFlag(ch.slug)}</span>}
                  {!isCountry && <MapPin className="h-2.5 w-2.5 text-muted-foreground shrink-0" />}
                  <span className="truncate">
                    {isCountry
                      ? (COUNTRY_NAMES[ch.slug] || ch.name || ch.slug.toUpperCase())
                      : (ch.name || formatGeoSlug(ch.slug))}
                  </span>
                  {!isCountry && (
                    <span className="text-muted-foreground uppercase ml-auto shrink-0">{ch.slug.split('/')[0]}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {search.trim() && results.length === 0 && (
          <p className="text-xs text-muted-foreground py-0.5">{t('discover.noLocationsFound')}</p>
        )}
      </div>
    </FilterSection>
  );
}
